import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Edit, Trash2, Plus, FileText, Target, CheckCircle, Clock, AlertCircle, Eye } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import auditSectionService from '../../../../../../services/audit_services/auditSectionService';
import auditQuestionService from '../../../../../../services/audit_services/auditQuestionService';

export default function AuditSectionDetails() {
  const { auditPlanId, id } = useParams();
  const { hasAnyRole, authTokens, user } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [auditSection, setAuditSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [userRoleInPlan, setUserRoleInPlan] = useState(null); // User's role in the audit plan
  const [auditPlanStatus, setAuditPlanStatus] = useState(null); // Audit plan status

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    isLoading: false
  });

  // Can edit if:
  // 1. User has AuditUser or AuditManager role
  // 2. User is an auditor in the audit plan
  // 3. Audit plan status is 'in_progress'
  const canEdit = hasAnyRole(['AuditUser','AuditManager']) && 
                  userRoleInPlan === 'auditor' && 
                  auditPlanStatus === 'in_progress';

  useEffect(() => {
    fetchAuditSectionDetails();
  }, [id]);

  useEffect(() => {
    if (auditSection) {
      fetchRecentQuestions();
      // Set user's role from the section detail response
      if (auditSection.my_role_in_audit_plan) {
        setUserRoleInPlan(auditSection.my_role_in_audit_plan);
      }
      // Set audit plan status
      if (auditSection.audit_plan?.status) {
        setAuditPlanStatus(auditSection.audit_plan.status);
      }
    }
  }, [auditSection]);

  const fetchAuditSectionDetails = async () => {
    setLoading(true);
    
    try {
      const result = await auditSectionService.getAuditSectionDetail(authTokens.access, auditPlanId, id);
      
      if (result.success) {
        setAuditSection(result.data);
      } else {
        showError(result.error || 'Erreur lors du chargement des détails de la section d\'audit');
      }
    } catch (error) {
      console.error('Error fetching audit section details:', error);
      showError('Erreur lors du chargement des détails de la section d\'audit');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentQuestions = async () => {
    setQuestionsLoading(true);
    
    try {
      // Get auditPlanId from the audit section data
      const planId = auditSection.audit_plan?.id || auditPlanId;
      
      if (!planId) {
        console.error('Audit plan ID not available');
        setRecentQuestions([]);
        setQuestionsLoading(false);
        return;
      }

      const result = await auditQuestionService.getAuditQuestions(
        authTokens.access,
        planId,
        id, // section id
        {
          ordering: 'created_at_desc' // Get most recent first
        },
        {
          page: 1,
          page_size: 5 // limit to 5 questions
        }
      );
      
      if (result.success) {
        setRecentQuestions(result.data.results || []);
      } else {
        console.error('Error loading recent questions:', result.error);
        setRecentQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching recent questions:', error);
      setRecentQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // const handleDelete = async () => {
  //   setDeleteModal(prev => ({ ...prev, isLoading: true }));
    
  //   try {
  //     // TODO: Add delete method to auditSectionService
  //     // const result = await auditSectionService.deleteAuditSection(authTokens.access, id);
  //     // if (result.success) {
  //       showSuccess(`Section "${auditSection.section_name}" deleted successfully`);
  //       navigate(`/dashboard/audit/audit-plans/${auditPlanId}/sections`);
  //     // } else {
  //     //   showError(result.error || 'Error deleting audit section');
  //     //   setDeleteModal(prev => ({ ...prev, isLoading: false }));
  //     // }
  //   } catch (error) {
  //     console.error('Error deleting audit section:', error);
  //     showError('Error deleting audit section');
  //     setDeleteModal(prev => ({ ...prev, isLoading: false }));
  //   }
  // };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En attente', icon: Clock },
      in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'En cours', icon: Target },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé', icon: CheckCircle },
      on_hold: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En pause', icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const getConfirmationBadge = (confirmation) => {
    const confirmationConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En attente' },
      confirmed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Confirmé' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejeté' },
      needs_clarification: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Besoin de clarification' }
    };

    const config = confirmationConfig[confirmation] || confirmationConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getQuestionStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En attente' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminé' },
      in_review: { bg: 'bg-green-100', text: 'text-green-800', label: 'En révision' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de la section...</p>
        </div>
      </div>
    );
  }

  if (!auditSection) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Section non trouvée</p>
        <Link to={`/dashboard/audit/audit-plans/${auditPlanId}/sections`} className="text-green-600 hover:underline mt-4 inline-block">
          Retour aux sections
        </Link>
      </div>
    );
  }



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/dashboard/audit/audit-plans/${auditPlanId}/sections`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <div className="flex items-center space-x-2 mb-1">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-600 text-sm font-semibold rounded-full">
                {auditSection.order}
              </span>
              <h1 className="text-2xl font-bold text-gray-900">{auditSection.section_name}</h1>
            </div>
            <p className="text-sm text-gray-600">
              Détails de la section
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(auditSection.status)}
          {canEdit && (
            <div className="flex items-center space-x-2">
              <Link
                to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${auditSection.id}/edit`}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Link>

            </div>
          )}
        </div>
      </div>

      {/* Audit Plan Link */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center">
          <Target className="h-5 w-5 text-green-600 mr-2" />
          <span className="text-sm text-green-900">
            Partie de :{' '}
            <Link
              to={`/dashboard/audit/my-plans/${auditSection.audit_plan.id}`}
              className="font-semibold hover:underline"
            >
              {auditSection.audit_plan.title}
            </Link>
          </span>
        </div>
      </div>

      {/* Status Message - Show when user is auditor but can't edit due to status */}
      {hasAnyRole(['AuditUser','AuditManager']) && userRoleInPlan === 'auditor' && auditPlanStatus !== 'in_progress' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-900">Modification restreinte</p>
              <p className="text-sm text-yellow-800 mt-1">
                Les sections ne peuvent être modifiées que lorsque le statut du plan d'audit est "En cours". 
                Statut actuel : <span className="font-semibold">{auditPlanStatus?.replace('_', ' ').toUpperCase()}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Section Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations de la section</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Nom de la section</p>
              <p className="text-sm text-gray-900">{auditSection.section_name}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Localisation</p>
              <p className="text-sm text-gray-900">{auditSection.location}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Créé par</p>
              <p className="text-sm text-gray-900">{auditSection.created_by_name}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Dernière mise à jour</p>
              <p className="text-sm text-gray-900">{new Date(auditSection.updated_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Plan Details */}
      {auditSection?.audit_plan && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Détails du plan d'audit</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Titre du plan</p>
                <Link
                  to={`/dashboard/audit/my-plans/${auditSection.audit_plan.id}`}
                  className="text-sm text-green-600 hover:text-green-900 font-medium"
                >
                  {auditSection.audit_plan.title}
                </Link>
              </div>

              {auditSection.audit_plan.objective && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Objectif</p>
                  <p className="text-sm text-gray-900">{auditSection.audit_plan.objective}</p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {auditSection.audit_plan.scope && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Portée</p>
                  <div className="text-sm text-gray-900">
                    {Array.isArray(auditSection.audit_plan.scope) ? (
                      <ul className="list-disc list-inside space-y-1">
                        {auditSection.audit_plan.scope.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>{auditSection.audit_plan.scope}</p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Statut du plan</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  auditSection.audit_plan.status === 'completed' ? 'bg-green-100 text-green-800' :
                  auditSection.audit_plan.status === 'in_progress' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {auditSection.audit_plan.status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Parties */}
          {auditSection.audit_plan.parties && auditSection.audit_plan.parties.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Parties d'audit</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {auditSection.audit_plan.parties.map((party, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{party.name}</p>
                      <p className="text-xs text-gray-500">{party.email}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      party.partyRole === 'auditor' ? 'bg-green-100 text-green-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {party.partyRole}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Risks */}
          {auditSection.audit_plan.risks && auditSection.audit_plan.risks.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium text-gray-700 mb-3">Risques associés</p>
              <div className="space-y-3">
                {auditSection.audit_plan.risks.slice(0, 3).map((risk, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{risk.name}</p>
                        <p className="text-xs text-gray-600 mt-1">{risk.description}</p>
                      </div>
                      <span className={`ml-3 px-2 py-1 text-xs font-medium rounded ${
                        risk.probability === 'critical' ? 'bg-red-100 text-red-800' :
                        risk.probability === 'high' ? 'bg-orange-100 text-orange-800' :
                        risk.probability === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {risk.probability}
                      </span>
                    </div>
                  </div>
                ))}
                {auditSection.audit_plan.risks.length > 3 && (
                  <p className="text-xs text-gray-500 text-center">
                    +{auditSection.audit_plan.risks.length - 3} risques supplémentaires
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Questions Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-green-600" />
            Questions récentes
          </h3>
          <div className="flex items-center space-x-3">
            <Link
              to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${id}/questions`}
              className="inline-flex items-center px-3 py-2 text-sm text-green-600 hover:text-green-900 font-medium"
            >
              <Eye className="h-4 w-4 mr-1" />
              Voir tout
            </Link>
            {canEdit && (
              <Link
                to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${id}/questions/create`}
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter une question
              </Link>
            )}
          </div>
        </div>

        {questionsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <span className="ml-3 text-gray-600">Chargement des questions...</span>
          </div>
        ) : recentQuestions.length > 0 ? (
          <div className="space-y-4">
            {recentQuestions.map((question) => (
              <div key={question.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Link
                        to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${id}/questions/${question.id}`}
                        className="text-sm font-medium text-gray-900 hover:text-green-600"
                      >
                        {question.question_name}
                      </Link>
                      {getQuestionStatusBadge(question.status)}
                      {question.auditee_response_confirmation && (
                        getConfirmationBadge(question.auditee_response_confirmation)
                      )}
                    </div>
                    
                    {question.location && (
                      <p className="text-xs text-gray-600 mb-2">
                        <span className="font-medium">Localisation :</span> {question.location}
                      </p>
                    )}
                    
                    {question.question_description && (
                      <p className="text-sm text-gray-700 overflow-hidden" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {question.question_description}
                      </p>
                    )}
                    
                    <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                      <span>Créé : {new Date(question.created_at).toLocaleDateString()}</span>
                      {question.updated_at !== question.created_at && (
                        <span>Mis à jour : {new Date(question.updated_at).toLocaleDateString()}</span>
                      )}
                      {question.associated_risk && (
                        <span className="text-orange-600">Risque : {question.associated_risk}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <Link
                      to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${id}/questions/${question.id}`}
                      className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                      title="Voir les détails"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                    {canEdit && (
                      <>
                        <Link
                          to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${id}/questions/${question.id}/edit`}
                          className="p-1 text-gray-400 hover:text-yellow-600 transition-colors"
                          title="Modifier la question"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Aucune question créée pour cette section</p>
            <div className="flex justify-center space-x-3">
              {canEdit ? (
                <Link
                  to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${id}/questions/create`}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ajouter la première question
                </Link>
              ) : (
                <p className="text-sm text-gray-500">Les questions apparaîtront ici une fois créées par les auditeurs</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {canEdit && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Link
              to={`/dashboard/audit/audit-plans/${auditPlanId}/sections/${auditSection.id}/questions`}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileText className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-sm font-medium text-gray-900">Gérer les questions</span>
            </Link>

            <Link
              to={`/dashboard/audit/reports?section=${auditSection.id}&audit_plan=${auditPlanId}`}
              className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileText className="h-5 w-5 text-purple-600 mr-2" />
              <span className="text-sm font-medium text-gray-900">Rapport de section</span>
            </Link>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {/* <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, isLoading: false })}
        onConfirm={handleDelete}
        title="Delete Audit Section"
        message={`Are you sure you want to delete "${auditSection?.section_name}"? This will also delete all associated questions and answers. This action cannot be undone.`}
        confirmText="Delete Section"
        cancelText="Cancel"
        isLoading={deleteModal.isLoading}
        type="danger"
      /> */}
    </div>
  );
}